import {
  GainType,
  ISelectedUnit,
  ISpecialRule,
  IUnit,
  IUpgradeDependency,
  IUpgradeGains,
  IUpgradeGainsItem,
  IUpgradeGainsRule,
  IUpgradeOption,
  IUpgradePackage,
  IUpgradeSection
} from "@/data/types";
import { findLastIndex, flatMap, groupBy, maxBy, orderBy, sumBy } from "lodash-es";
import { nanoid } from "nanoid";
import { Result } from "typescript-result";
import ArmyBookItem from "./armyBook/ArmyBookItem";
import ArmyBookWeapon from "./armyBook/ArmyBookWeapon";
import CalcService from "./calc/calcService";
import { UpgradeCalcService } from "./calc/UpgradeCalcService";
import DataParsingService from "./DataParsingService";
import EquipmentService from "./EquipmentService";
import { forEachTarget, isItem, isRule, isWeapon, makeCopy } from "./Helpers";
import RulesService from "./rules/RulesService";
import UnitService from "./UnitService";

interface IUpgradeableUnit {
  loadout: IUpgradeGains[];
  weapons: IUpgradeGains[];
  items: IUpgradeGainsItem[];
  selectedUpgrades: { instanceId: string; upgrade: IUpgradeSection; option: IUpgradeOption }[];
  rules: ISpecialRule[];
  size: number;
}

export default class UpgradeService {
  public static buildUpgrades<T extends IUpgradeableUnit>(unit: T) {
    if (!unit) return null;

    unit.loadout = makeCopy(unit.weapons.concat(unit.items || []));

    for (const upgrade of makeCopy(unit.selectedUpgrades || [])) {
      try {
        this.applyUpgrade(unit, upgrade.upgrade, upgrade.option);
      } catch {
        // console.error(e);
        // console.error("Error loading upgrade", upgrade.option);
        // console.error("For section", upgrade.upgrade);
        // console.error("For unit", unit);
      }
    }

    return unit;
  }

  private static applyUpgrade(unit: IUpgradeableUnit, upgrade: IUpgradeSection, option: IUpgradeOption) {
    //debugger;
    const affectsCount = this.getAffectsCount(unit, upgrade);
    const isAffectsAll = upgrade.affects?.type === "all";
    const isReplace = upgrade.variant === "replace";
    const isReplaceAll = upgrade.variant === "replace" && upgrade.affects?.type === "all";
    const countMultiplier = isReplace
      ? isReplaceAll
        ? this.getMaxRemoveCount(unit, upgrade)
        : 1 // Don't modify the count unless it's a replace all
      : affectsCount;

    const gainEquipment = option.gains
      .filter((item) => item.type === GainType.Item || item.type === GainType.Weapon)
      .map((g) => ({
        ...g,
        count: (g.count || 1) * countMultiplier,
        content:
          g.type === GainType.Item
            ? (g as IUpgradeGainsItem).content.map((c) => ({
                ...c,
                count: (c.count || 1) * (g.count || 1) * countMultiplier
              }))
            : undefined
      }));

    if (upgrade.variant === "upgradeRule") {
      // TODO: Refactor this - shouldn't be using display name func to compare probably!
      const existingRuleIndex = unit.rules.findIndex(
        (r) => RulesService.displayName(r) === (upgrade.targets[0] as string)
      );

      // Remove existing rule
      if (existingRuleIndex > -1) unit.rules.splice(existingRuleIndex, 1);

      return;
    } else if (upgrade.variant === "upgrade" || upgrade.variant === "attachment") {
      // Upgrade weapon with
      if (upgrade.targets) {
        // For each thing we're gaining
        for (const gain of option.gains) {
          const applyToTarget = (x: IUpgradeGainsRule) => {
            forEachTarget(upgrade, (target) => {
              // Find a matching weapon...
              const upgradeTarget = unit.loadout
                .filter((e) => EquipmentService.compareEquipment(e, target))
                // ...that doesn't already have this rule
                .find((x) => {
                  if (isItem(x)) {
                    // Check the content doesn't contain the rule
                    return x.content.every((c) => c.name !== x.name);
                  } else if (isWeapon(x)) {
                    return x.specialRules.every((rule) => rule.name !== x.name);
                  }
                  return false;
                });

              if (!upgradeTarget) return;

              const clone = makeCopy(upgradeTarget);

              // Reduce / remove the original upgrade target...
              upgradeTarget.count -= countMultiplier;
              if (upgradeTarget.count <= 0) {
                unit.loadout.splice(unit.loadout.indexOf(upgradeTarget), 1);
              }

              // ...to make space for the new modified one
              clone.count = countMultiplier;

              if (isWeapon(clone)) {
                clone.specialRules.push(x);
                clone.label = ArmyBookWeapon.FromObject(clone).toString();
              } else if (isItem(clone)) {
                clone.content.push(x);
                clone.label = ArmyBookItem.FromObject(clone).toString();
              }

              unit.loadout.push(clone);
            });
          };

          if (isWeapon(gain)) {
            unit.loadout.push({
              ...gain,
              count: (gain.count || 1) * countMultiplier
            });
          }

          if (isItem(gain)) {
            // TODO ?
            unit.loadout.push({
              ...gain,
              count: (gain.count || 1) * countMultiplier
            });
            // for (const c of gain.content) {
            //   if (isRule(c)) {
            //     applyToTarget(c);
            //   }
            // }
          }

          if (isRule(gain)) {
            // Check the targets
            applyToTarget(gain);
          }
        }
      } else {
        unit.loadout = unit.loadout.concat(gainEquipment);
      }
    } else if (upgrade.variant === "replace") {
      let removeCount = isAffectsAll ? this.getMaxRemoveCount(unit, upgrade) : affectsCount;
      const allEquipment = UnitService.getAllEquipment(unit);

      forEachTarget(upgrade, (target, count) => {
        let removed = 0;
        const removeCountThisTarget = count * removeCount;

        // Item
        const replaceItem = unit.loadout
          .filter((x) => isItem(x))
          .find((e) => EquipmentService.compareEquipment(e, target));

        if (replaceItem) {
          replaceItem.count -= removeCount * (count || 1);
          return;
        }

        // Weapon
        const toReplace = allEquipment.filter((e) => EquipmentService.compareEquipment(e, target));
        if (toReplace.length === 0) {
        }
        for (const weapon of toReplace) {
          if (removed < removeCountThisTarget) {
            const howManyOfThisWeaponCanWeRemove = Math.min(removeCountThisTarget - removed, weapon.count);
            removed += howManyOfThisWeaponCanWeRemove;
            weapon.count -= howManyOfThisWeaponCanWeRemove;
          }
        }
      });

      unit.loadout = unit.loadout.concat(gainEquipment);
    }

    // Remove upgrade/attachments that rely on something that is being removed
    const toRemove = unit.loadout.filter((e) => e.count <= 0);
    for (let item of toRemove) {
      for (let dep of (item.dependencies ?? []).filter(
        (dep) => dep.variant === "upgrade" || dep.variant === "attachment"
      )) {
        this.removeById(unit, dep.upgradeInstanceId);
      }
    }

    // Remove entires with no count left
    unit.loadout = unit.loadout.filter((e) => e.count > 0);
    for (let item of unit.loadout.filter((i) => i.type === GainType.Item) as IUpgradeGainsItem[]) {
      item.content = item.content.filter(
        (c) =>
          c.count === undefined || // Keep items that don't have a count property at all
          c.count > 0 // Items that have a count
      );
    }
  }

  public static isApplied(unit: ISelectedUnit, upgrade: IUpgradeSection, option: IUpgradeOption): boolean {
    return unit.selectedUpgrades.map((su) => su.option).some((u) => u.uid === option.uid);
  }

  public static countApplied(unit: ISelectedUnit, upgrade: IUpgradeSection, option: IUpgradeOption): number {
    return unit.selectedUpgrades.map((su) => su.option).filter((u) => u.uid === option.uid).length;
  }

  public static countAvailable(unit: ISelectedUnit, upgrade: IUpgradeSection) {
    const appliedInGroup = upgrade.options.reduce(
      (total, opt) => total + this.countApplied(unit, upgrade, opt),
      0
    );
    const groups = groupBy(UnitService.getAllEquipment(unit), (e) => e.name);

    let available = 999;

    forEachTarget(upgrade, (target) => {
      const groupKey = Object.keys(groups).find((k) => EquipmentService.compareEquipmentNames(k, target));

      const toReplace: IUpgradeGains[] = groups[groupKey];
      if (!toReplace) return (available = 0);

      const toReplaceCount = toReplace.reduce((count, gain) => count + gain.count, 0);
      available = Math.min(toReplaceCount, available);

      if (upgrade.select?.type === "any") {
        const total = unit.size * (upgrade.affects?.value || 1);
        available = Math.min(available, total - appliedInGroup);
      } else if (upgrade.affects?.type === "up to") {
        available = Math.min(available, upgrade.affects.value - appliedInGroup);
      } else if (upgrade.affects?.type === "exactly") {
        available = Math.min(available, upgrade.affects.value - appliedInGroup);
      }
      // TODO: SELECT type upgrades...
      // May only select up to the limit
      else if (typeof upgrade.select === "number") {
        // Any model may replace 1...
        if (upgrade.affects?.type === "any") {
          available = Math.min(available, upgrade.select * unit.size);
        } else {
          available = Math.min(available, (upgrade.select as any) - appliedInGroup);
        }
      }
    });

    return available;
  }

  public static getControlType(upgrade: IUpgradeSection): "check" | "radio" | "updown" {
    if (upgrade.isCommandGroup) return "check";

    if (upgrade.variant === "upgrade") {
      if (upgrade.select?.value === 1 && !(upgrade.affects?.value > 1)) {
        // "Upgrade any x with one", but not "upgrade any model with one"
        if (upgrade.affects?.type === "any" && !upgrade.model) return "updown";
        return "radio";
      }

      if (["up to", "any"].includes(upgrade.affects?.type)) {
        return "updown";
      }
      if (upgrade.select?.type === "up to") {
        return "updown";
      }
      return "check";
    }

    if (upgrade.variant === "attachment") {
      const isAnyModel = upgrade.select?.type === "any" && upgrade.model;
      if (isAnyModel) {
        return "updown";
      }

      if (upgrade.select?.value === 1) return "radio";
    }

    if (upgrade.variant === "replace") {
      if (
        upgrade.select?.type === "any" ||
        upgrade.affects?.type === "any" ||
        upgrade.affects?.type === "up to"
      ) {
        return "updown";
      }

      return "radio";
    }
    debugger;
    console.warn("No control type for: ", upgrade);

    return "updown";
  }

  public static isValid(
    unit: ISelectedUnit,
    upgradeSection: IUpgradeSection,
    option: IUpgradeOption
  ): boolean {
    const controlType = this.getControlType(upgradeSection);
    const applied = this.countApplied(unit, upgradeSection, option);
    const appliedInGroup = upgradeSection.options.reduce(
      (total, opt) => total + this.countApplied(unit, upgradeSection, opt),
      0
    );

    if (applied >= 99) return false;

    // if it's a radio, it's valid if any other upgrade in the group is already applied
    if (controlType === "radio") if (appliedInGroup > 0) return true;

    // if it's a checkbox that is already ticked, it's valid
    if (controlType === "check") if (applied > 0) return true;

    if (upgradeSection.variant === "replace") {
      const requiredCount = upgradeSection.affects?.type === "exactly" ? upgradeSection.affects.value : 1;

      const groups = groupBy(UnitService.getAllEquipment(unit), (e) => e.name || e.label);

      let valid = true;
      forEachTarget(upgradeSection, (target, count) => {
        const requiredCountForTarget = requiredCount * (count ?? 1);

        const groupKey = Object.keys(groups).find((k) => EquipmentService.compareEquipmentNames(k, target));

        const toReplace: IUpgradeGains[] = groups[groupKey];
        if (!toReplace) return (valid = false);

        const toReplaceCount = toReplace.reduce((count, gain) => count + gain.count, 0);

        // Would not have enough to replace
        if (requiredCountForTarget > toReplaceCount) valid = false;

        if (upgradeSection.select?.type === "any") {
          const max = unit.size * (upgradeSection.affects?.value || 1);
          if (appliedInGroup >= max) {
            return (valid = false);
          }
        } else if (upgradeSection.affects?.type === "up to") {
          if (appliedInGroup >= upgradeSection.affects.value) {
            return (valid = false);
          }
        } else if (unit.combined && upgradeSection.affects?.value === 1 && appliedInGroup >= 2) {
          return (valid = false);
        }
      });
      if (!valid) return false;
    }

    if (upgradeSection.variant === "upgrade" || upgradeSection.variant === "attachment") {
      // upgrade (n? (models|weapons)?) with...
      var available = unit.size;

      // if replacing equipment, count number of those equipment available
      if (upgradeSection.targets) {
        forEachTarget(upgradeSection, (what) => {
          available = unit.loadout
            // Take only the gains that match this dependency
            .filter((g) => EquipmentService.compareEquipment(g, what))
            // Count how many we have
            .reduce((count, next) => count + next.count, 0);
        });

        // The thing we're targetting does not exist
        if (available === 0 && appliedInGroup === 0) return false;
        if (appliedInGroup >= available) return false;
      }

      // Upgrade any of any is always valid
      if (upgradeSection.affects?.type === "any" && upgradeSection.select?.type === "any") {
        return true;
      }

      // "Any model"
      if (upgradeSection.select?.type === "any" && upgradeSection.model) {
        const max = unit.size * (upgradeSection.affects?.value || 1);
        if (appliedInGroup >= max) {
          return false;
        }
      } else if (upgradeSection.affects?.value) {
        return appliedInGroup < upgradeSection.affects.value;
      }

      // Upgrade with up to two // TODO: Model?
      if (upgradeSection.select?.type === "up to") {
        return appliedInGroup < upgradeSection.select.value;
      }

      // Upgrade [(any)?] with n:
      if (upgradeSection.select?.type === "exactly") {
        if (upgradeSection.affects?.type === "any") {
          if (appliedInGroup >= upgradeSection.select.value * available) {
            return false;
          }
        } else if (appliedInGroup >= upgradeSection.select.value) {
          return false;
        }

        if (upgradeSection.affects?.value) {
          const max = upgradeSection.affects.value * upgradeSection.select.value;
          if (appliedInGroup >= max) {
            return false;
          }
        }
      }
      // Upgrade any
      else if (upgradeSection.affects?.type === "any" && appliedInGroup >= available) {
        return false;
      }
    }

    return true;
  }

  public static apply(
    unit: IUpgradeableUnit,
    upgrade: IUpgradeSection,
    option: IUpgradeOption,
    instanceId: string = null,
    index: number = null
  ) {
    const upgradeInstanceId = instanceId || nanoid(9);
    const isUpgradeAll = upgrade.variant === "upgrade" && upgrade.affects?.type === "all";

    const optionToApply = {
      ...option,
      gains: option.gains.map((g) => ({
        ...g,
        count: isUpgradeAll && g.type === GainType.Rule ? (g.count ?? 1) * unit.size : g.count,
        dependencies: [],
        content:
          g.type === GainType.Item
            ? (g as IUpgradeGainsItem).content.map((c) => ({
                ...c,
                dependencies: []
              }))
            : undefined
      }))
    };

    const { options: _options, ...section } = upgrade;

    const upgradeInstance = {
      instanceId: upgradeInstanceId,
      upgrade: section,
      option: optionToApply
    };

    // Add this upgrade to the unit
    if (index !== null) {
      unit.selectedUpgrades.splice(index, 0, upgradeInstance);
    } else {
      unit.selectedUpgrades.push(upgradeInstance);
    }

    // Figure out deps...
    if (upgrade.targets?.length > 0) {
      this.applyDependencies(upgradeInstanceId, unit, upgrade);
    }

    // Apply hero name
    // if (upgrade.isHeroUpgrade) {
    //   const existingName = (unit as any).customName;
    //   const heroNames = upgrade.options.map((x) => x.gains[0].name);
    //   if (!existingName || heroNames.includes(existingName)) {
    //     const newName = optionToApply.gains[0].name;
    //     (unit as any).customName = newName;
    //   }
    // }

    unit = this.buildUpgrades(unit) as any as ISelectedUnit;

    return upgradeInstanceId;
  }

  public static applyDependencies(
    upgradeInstanceId: string,
    unit: IUpgradeableUnit,
    upgrade: IUpgradeSection
  ) {
    const affectsCount = this.getAffectsCount(unit, upgrade);

    // Find a thing to depend on for each thing we're replacing
    forEachTarget(upgrade, (target) => {
      let remainingToReplace = affectsCount;

      const applyDependency = (equipment: IUpgradeGains[], affectsAll: boolean) => {
        const weapon = equipment.find((g) => EquipmentService.compareEquipment(g, target));
        if (!weapon) return;
        if (!weapon.dependencies) weapon.dependencies = [];

        // gain is a thing we're looking for, check to see if it can be depended upon
        const alreadyTaken = weapon.dependencies
          .filter((dep) => dep.variant === "replace")
          .reduce((count, dep) => count + dep.count, 0);

        const loadoutWeapon =
          affectsAll &&
          unit.loadout
            .concat(unit.loadout.flatMap((x) => (isItem(x) ? x.content : [])))
            .find((x) => EquipmentService.compareEquipment(x, target));

        const remainingAvailable = affectsAll ? (loadoutWeapon?.count ?? 0) : weapon.count - alreadyTaken;

        // The lesser of "the amount we have" vs "the amount we need"
        const count = Math.min(remainingAvailable, remainingToReplace);
        if (count > 0) {
          weapon.dependencies.push({
            upgradeInstanceId,
            count: count,
            variant: upgrade.variant
          });

          remainingToReplace -= count;
        }
      };

      // Try and depend on equipment before anything else
      applyDependency(unit.weapons, false);

      // -1 because we've added "this" upgrade already above, and want to skip it
      const startAtIndex = unit.selectedUpgrades.length - 1;

      // Check each applied upgrade, in reverse order until we find a thing to depend upon
      for (let i = startAtIndex; i > 0; i--) {
        // Stop looking if we've replaced enough
        if (remainingToReplace <= 0) break;

        const upgrade = unit.selectedUpgrades[i - 1];

        applyDependency(this.getAllGains(upgrade.option), upgrade.upgrade.affects?.type === "all");
      }
    });
  }

  public static getAllGains(option: IUpgradeOption) {
    const nestedItems = flatMap(
      option.gains.filter((e) => e.type === GainType.Item),
      (e: IUpgradeGainsItem) => e.content
    );

    return option.gains.concat(nestedItems);
  }

  public static getAffectsCount(unit: IUpgradeableUnit, upgrade: IUpgradeSection) {
    return upgrade.affects?.type === "exactly"
      ? upgrade.affects?.value
      : upgrade.affects?.type === "all"
        ? unit.size || 1 // All in unit
        : 1;
  }

  public static getMaxRemoveCount(unit: IUpgradeableUnit, upgrade: IUpgradeSection) {
    const allEquipment = UnitService.getAllEquipment(unit as ISelectedUnit);
    let removeCount = this.getAffectsCount(unit, upgrade);

    // For each thing we're replacing
    forEachTarget(upgrade, (target) => {
      const toReplace =
        allEquipment.filter((e) => EquipmentService.compareEquipment(e, target)) ??
        unit.items.filter((x) => EquipmentService.compareEquipment(x, target));
      if (!toReplace.length) {
        console.log("getMaxRemoveCount could not find ", target);
      }
      const toReplaceCount = sumBy(toReplace, (g) => g.count || 1);
      // Check how many there are available to replace
      if (toReplaceCount < removeCount) removeCount = toReplaceCount;
    });

    return removeCount;
  }

  public static remove(unit: ISelectedUnit, option: IUpgradeOption) {
    console.log("Removing option...", JSON.parse(JSON.stringify(option)));

    const removeId = this.getRemoveId(unit, option);

    this.removeById(unit, removeId);
  }

  public static getRemoveId(unit: ISelectedUnit, option: IUpgradeOption) {
    for (let i = unit.selectedUpgrades.length - 1; i >= 0; i--) {
      const upgrade = unit.selectedUpgrades[i];
      if (upgrade.option.uid === option.uid) return upgrade.instanceId;
    }
    return null;
  }

  public static removeById(unit: IUpgradeableUnit, optionInstanceId: string) {
    const removeAt = findLastIndex(unit.selectedUpgrades, (u) => u.instanceId === optionInstanceId);
    const toRemove = unit.selectedUpgrades[removeAt];

    console.log("Removing...", makeCopy(toRemove));

    for (let gain of toRemove.option.gains) {
      if (gain.type === GainType.Item) {
        for (let nestedItem of (gain as IUpgradeGainsItem).content) {
          for (let dep of makeCopy(nestedItem.dependencies)) {
            this.removeById(unit, dep.upgradeInstanceId);
          }
        }
      }
      for (let dep of makeCopy(gain.dependencies)) {
        this.removeById(unit, dep.upgradeInstanceId);
      }
    }

    // Remove the upgrade
    unit.selectedUpgrades.splice(removeAt, 1);

    const removeFromDeps = (deps: IUpgradeDependency[]) => {
      const idx = deps.findIndex((d) => d.upgradeInstanceId === optionInstanceId);
      if (idx >= 0) deps.splice(idx, 1);
    };

    // Remove this item from dependencies of other items
    for (let opt of unit.weapons) removeFromDeps(opt.dependencies ?? []);

    const allGainsOfAllUpgrades = unit.selectedUpgrades.map((x) => this.getAllGains(x.option));
    for (let opt of flatMap(allGainsOfAllUpgrades, (x) => x) as IUpgradeGains[]) {
      removeFromDeps(opt.dependencies ?? []);
    }

    // Expensive to put this here but guarantees it'll be up to date!
    unit = this.buildUpgrades(unit);
  }

  public static sortPackages(bookName: string, units: IUnit[], bookPackages: IUpgradePackage[]) {
    const upgradePackagesIds = (() => {
      const upgradePackagesIds = [];
      units
        .filter((unit) => unit.upgrades) // only units with upgrades
        .forEach((unit) => upgradePackagesIds.push(...unit.upgrades));
      return [...new Set(upgradePackagesIds)];
    })();

    const sortedPackages = (() => {
      const upgradePackages = [];
      const allPackages = [...bookPackages];
      const doQuestSort = bookName === "_Quest";
      if (doQuestSort) return orderBy(allPackages, "hint");
      upgradePackagesIds.forEach((upgradePackageId) => {
        const index = allPackages.findIndex((up) => up.uid === upgradePackageId);
        if (index >= 0) {
          const found = allPackages.splice(index, 1);
          upgradePackages.push(...found);
        } else {
          // console.info(`no package found for ${upgradePackageId}`, this.armyBookId);
        }
      });
      upgradePackages.push(...allPackages);
      return upgradePackages;
    })();

    return sortedPackages;
  }

  public static transformSection(unit: ISelectedUnit, section: IUpgradeSection): IUpgradeSection {
    // For now, all this is doing is "Upgrade all models with:" -> "Upgrade with:"
    if (unit.size !== 1 || !section.label.includes("all models")) return section;

    const newSection = makeCopy(section);

    delete newSection.select;
    delete newSection.affects;
    delete newSection.targets;

    newSection.label = newSection.label.replace("all models with", "with");

    const upgrade = DataParsingService.parseUpgradeText(newSection.label);

    return { ...newSection, ...upgrade };
  }

  public static isOptionSwappable(
    calcService: CalcService,
    pkg: IUpgradePackage,
    upgradeSection: IUpgradeSection,
    option: IUpgradeOption,
    optionCostResult: Result<{ newCostPrecise: number }[], Error>,
    usingUnits: IUnit[]
  ) {
    const isSwappableSection = upgradeSection.variant === "replace" && upgradeSection.affects?.value !== 1;
    const thisMaxCost = maxBy(option.costs, (c) => c.exactCost ?? c.cost)?.exactCost ?? 0;
    const otherOptions = upgradeSection.options.filter((o) => o.uid !== option.uid);
    const isNegativeUpgrade =
      optionCostResult.ok && optionCostResult.value.every((x) => x.newCostPrecise < 0);
    const isCheapestOption = !otherOptions.some(
      (o) => maxBy(o.costs, (c) => c.exactCost ?? c.cost)?.exactCost < thisMaxCost
    );
    const canSwapBase = usingUnits.every((u) =>
      // Each unit must have every target in their weapons to be eligible for swapping
      (upgradeSection.targets ?? []).every(
        (t) =>
          u.weapons.some((w) => EquipmentService.compareEquipmentNames(t, w.name)) ||
          u.items.some((w) => EquipmentService.compareEquipmentNames(t, w.name))
      )
    );

    const canSwapUpgrade =
      !canSwapBase &&
      isCheapestOption &&
      (() => {
        const otherSections = pkg.sections.filter((s) => s.uid !== upgradeSection.uid);
        for (const unit of usingUnits) {
          const reqs = new UpgradeCalcService(calcService).findMissingPrerequisites(
            unit,
            upgradeSection,
            otherSections
          );
          if (reqs.option) {
            return true; //debugger;
          }
        }
        return false;
      })();

    // Removed requirement for this to be only swapping weapons
    //option.gains.every((g) => isWeapon(g)) &&
    const isSwappable =
      isNegativeUpgrade && isSwappableSection && isCheapestOption && (canSwapBase || canSwapUpgrade);

    return { canSwapBase, isSwappable };
  }
}
